import { useState, useEffect, useCallback } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import ModuleGate from "@/components/base/ModuleGate";

/* ─── Types ─────────────────────────────────────────────────── */
interface CalendarSettings {
  calendar_email: string;
  sync_enabled: boolean;
  default_event_duration_minutes: string;
  timezone: string;
  include_location: boolean;
  include_client_name: boolean;
  include_notes: boolean;
  event_title_format: string;
  reminders_enabled: boolean;
  reminder_minutes_before: string;
}

const DEFAULT: CalendarSettings = {
  calendar_email: "",
  sync_enabled: false,
  default_event_duration_minutes: "120",
  timezone: "America/New_York",
  include_location: true,
  include_client_name: true,
  include_notes: false,
  event_title_format: "job_type_client",
  reminders_enabled: true,
  reminder_minutes_before: "60",
};

const TIMEZONES = [
  { value: "America/New_York",      label: "Eastern Time (US & Canada)" },
  { value: "America/Chicago",       label: "Central Time (US & Canada)" },
  { value: "America/Denver",        label: "Mountain Time (US & Canada)" },
  { value: "America/Los_Angeles",   label: "Pacific Time (US & Canada)" },
  { value: "America/Phoenix",       label: "Arizona (no DST)" },
  { value: "America/Mexico_City",   label: "Mexico City" },
  { value: "America/Bogota",        label: "Bogotá, Colombia" },
  { value: "America/Lima",          label: "Lima, Peru" },
  { value: "America/Santiago",      label: "Santiago, Chile" },
  { value: "America/Sao_Paulo",     label: "São Paulo, Brazil" },
  { value: "America/Buenos_Aires",  label: "Buenos Aires, Argentina" },
  { value: "America/Caracas",       label: "Caracas, Venezuela" },
  { value: "America/Toronto",       label: "Toronto, Canada" },
  { value: "America/Vancouver",     label: "Vancouver, Canada" },
  { value: "Europe/London",         label: "London (GMT/BST)" },
  { value: "Europe/Madrid",         label: "Madrid, Spain" },
  { value: "Europe/Paris",          label: "Paris, France" },
  { value: "Europe/Berlin",         label: "Berlin, Germany" },
  { value: "Europe/Rome",           label: "Rome, Italy" },
  { value: "Europe/Amsterdam",      label: "Amsterdam, Netherlands" },
  { value: "Asia/Dubai",            label: "Dubai (UAE)" },
  { value: "Asia/Tokyo",            label: "Tokyo, Japan" },
  { value: "Australia/Sydney",      label: "Sydney, Australia" },
  { value: "Pacific/Auckland",      label: "Auckland, New Zealand" },
];

const DURATIONS = [
  { value: "30",   label: "30 minutes" },
  { value: "60",   label: "1 hour" },
  { value: "90",   label: "1.5 hours" },
  { value: "120",  label: "2 hours" },
  { value: "180",  label: "3 hours" },
  { value: "240",  label: "4 hours" },
  { value: "300",  label: "5 hours" },
  { value: "360",  label: "6 hours" },
  { value: "480",  label: "8 hours (Full day)" },
  { value: "600",  label: "10 hours" },
  { value: "720",  label: "12 hours" },
];

const TITLE_FORMATS = [
  { value: "job_type_client",  label: "Job Type + Client",    example: "Wedding · Sarah & Tom" },
  { value: "client_job_type",  label: "Client + Job Type",    example: "Sarah & Tom · Wedding" },
  { value: "job_title_only",   label: "Job Title Only",       example: "Johnson Wedding — Full Day" },
  { value: "client_only",      label: "Client Name Only",     example: "Sarah Johnson" },
];

/* ─── Toggle ─────────────────────────────────────────────────── */
interface ToggleProps {
  enabled: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  color?: string;
}
function Toggle({ enabled, onChange, disabled, color = "#34A853" }: ToggleProps) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${
        disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
      }`}
      style={{ backgroundColor: enabled ? color : "#e2e8f0" }}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
          enabled ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

/* ─── Page ──────────────────────────────────────────────────── */
export default function AdminCalendarSettings() {
  const navigate = useNavigate();
  const [tenantId, setTenantId]   = useState<string | null>(null);
  const [rowId, setRowId]         = useState<string | null>(null);
  const [form, setForm]           = useState<CalendarSettings>(DEFAULT);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  /* ─── Fetch ── */
  const fetchSettings = useCallback(async (tid: string) => {
    const { data } = await supabase
      .from("tenant_integrations")
      .select("id, is_enabled, settings")
      .eq("tenant_id", tid)
      .eq("integration_key", "google_calendar")
      .maybeSingle();

    if (data) {
      setRowId(data.id);
      const s = (data.settings ?? {}) as Record<string, string>;
      setForm({
        calendar_email:                  s.calendar_email ?? "",
        sync_enabled:                    data.is_enabled ?? false,
        default_event_duration_minutes:  s.default_event_duration_minutes ?? "120",
        timezone:                        s.timezone ?? "America/New_York",
        include_location:                s.include_location !== "false",
        include_client_name:             s.include_client_name !== "false",
        include_notes:                   s.include_notes === "true",
        event_title_format:              s.event_title_format ?? "job_type_client",
        reminders_enabled:               s.reminders_enabled !== "false",
        reminder_minutes_before:         s.reminder_minutes_before ?? "60",
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchSettings(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchSettings]);

  /* ─── Save ── */
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setSaving(true);
    setSaved(false);
    setSaveError(null);

    const settingsJson: Record<string, string> = {
      calendar_email:                 form.calendar_email,
      default_event_duration_minutes: form.default_event_duration_minutes,
      timezone:                       form.timezone,
      include_location:               form.include_location ? "true" : "false",
      include_client_name:            form.include_client_name ? "true" : "false",
      include_notes:                  form.include_notes ? "true" : "false",
      event_title_format:             form.event_title_format,
      reminders_enabled:              form.reminders_enabled ? "true" : "false",
      reminder_minutes_before:        form.reminder_minutes_before,
    };

    const now = new Date().toISOString();
    const payload = {
      tenant_id:       tenantId,
      integration_key: "google_calendar",
      is_enabled:      form.sync_enabled,
      settings:        settingsJson,
      updated_at:      now,
    };

    if (rowId) {
      const { error } = await supabase
        .from("tenant_integrations")
        .update(payload)
        .eq("id", rowId);
      if (error) {
        setSaveError(error.message);
        setSaving(false);
        return;
      }
    } else {
      const { data, error } = await supabase
        .from("tenant_integrations")
        .insert({ ...payload, created_at: now })
        .select("id")
        .maybeSingle();
      if (error) {
        setSaveError(error.message);
        setSaving(false);
        return;
      }
      if (data) setRowId(data.id);
    }

    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const set = <K extends keyof CalendarSettings>(key: K, val: CalendarSettings[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const selectedFormat = TITLE_FORMATS.find((f) => f.value === form.event_title_format);

  return (
    <AdminLayout>
      <AdminHeader
        title="Google Calendar"
        subtitle="Configure calendar sync for your shoots and events"
      />

      <ModuleGate moduleId="appointments">
      <div className="px-4 md:px-8 py-4 md:py-7">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-[#34A853] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleSave}>
            <div className="grid grid-cols-3 gap-6">

              {/* ─── Left col (settings) ─── */}
              <div className="col-span-2 space-y-5">

                {/* Enable card */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-11 h-11 flex items-center justify-center rounded-xl transition-all"
                        style={{
                          backgroundColor: form.sync_enabled
                            ? "rgba(52,168,83,0.15)"
                            : "#f1f5f9",
                        }}
                      >
                        <i
                          className="ri-calendar-event-line text-xl"
                          style={{ color: form.sync_enabled ? "#34A853" : "#94a3b8" }}
                        />
                      </div>
                      <div>
                        <p className="text-[#0f172a] font-semibold text-sm">Calendar Sync</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              form.sync_enabled ? "bg-emerald-400" : "bg-slate-300"
                            }`}
                          />
                          <span
                            className={`text-xs ${
                              form.sync_enabled ? "text-emerald-600" : "text-slate-400"
                            }`}
                          >
                            {form.sync_enabled ? "Sync enabled" : "Sync disabled"}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Sync toggle disabled until OAuth wired */}
                    <div className="flex items-center gap-2.5">
                      <Toggle
                        enabled={form.sync_enabled}
                        onChange={(v) => set("sync_enabled", v)}
                        disabled={false}
                        color="#34A853"
                      />
                    </div>
                  </div>

                  {/* OAuth notice */}
                  <div className="flex items-start gap-2.5 px-3.5 py-3 bg-emerald-50 rounded-xl border border-emerald-200">
                    <i className="ri-shield-keyhole-line text-[#34A853] text-xs mt-0.5 shrink-0" />
                    <p className="text-emerald-700 text-[11px] leading-relaxed">
                      Google OAuth credentials must be stored in{" "}
                      <span className="font-mono">Supabase Edge Function secrets</span>. This page
                      prepares the configuration — no calendar events will be created until OAuth is
                      wired in the backend.
                    </p>
                  </div>
                </div>

                {/* Calendar identity */}
                <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
                  <h3 className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
                    <i className="ri-google-line text-[#34A853] text-base" />
                    Calendar Account
                  </h3>

                  <div>
                    <label className="text-slate-400 text-xs mb-1.5 block">Calendar Email / ID</label>
                    <input
                      type="email"
                      value={form.calendar_email}
                      onChange={(e) => set("calendar_email", e.target.value)}
                      placeholder="yourstudio@gmail.com"
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#34A853]/50 transition-colors"
                    />
                    <p className="text-slate-300 text-[11px] mt-1">
                      Google Calendar ID. Usually your Gmail address for the primary calendar, or the
                      calendar&apos;s email address if using a secondary calendar.
                    </p>
                  </div>
                </div>

                {/* Event defaults */}
                <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
                  <h3 className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
                    <i className="ri-time-line text-[#34A853] text-base" />
                    Event Defaults
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-slate-400 text-xs mb-1.5 block">Default Event Duration</label>
                      <select
                        value={form.default_event_duration_minutes}
                        onChange={(e) => set("default_event_duration_minutes", e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#34A853]/50 transition-colors cursor-pointer appearance-none"
                      >
                        {DURATIONS.map((d) => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                      <p className="text-slate-300 text-[11px] mt-1">
                        Used when shoot duration isn&apos;t specified on the job.
                      </p>
                    </div>

                    <div>
                      <label className="text-slate-400 text-xs mb-1.5 block">Timezone</label>
                      <select
                        value={form.timezone}
                        onChange={(e) => set("timezone", e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#34A853]/50 transition-colors cursor-pointer appearance-none"
                      >
                        {TIMEZONES.map((tz) => (
                          <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                      </select>
                      <p className="text-slate-300 text-[11px] mt-1">
                        Calendar events will be created in this timezone.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Event title format */}
                <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
                  <h3 className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
                    <i className="ri-text-wrap text-[#34A853] text-base" />
                    Event Title Format
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {TITLE_FORMATS.map((fmt) => {
                      const active = form.event_title_format === fmt.value;
                      return (
                        <button
                          key={fmt.value}
                          type="button"
                          onClick={() => set("event_title_format", fmt.value)}
                          className={`flex flex-col gap-1.5 p-3.5 rounded-xl border transition-all cursor-pointer text-left ${
                            active
                              ? "border-emerald-200 bg-emerald-50"
                              : "border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300"
                          }`}
                        >
                          <p
                            className={`text-xs font-semibold ${
                              active ? "text-[#34A853]" : "text-slate-400"
                            }`}
                          >
                            {fmt.label}
                          </p>
                          <p className="text-slate-400 text-[11px] font-mono">{fmt.example}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Reminders */}
                <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
                  <h3 className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
                    <i className="ri-alarm-line text-[#34A853] text-base" />
                    Reminders
                  </h3>

                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-slate-600 text-xs font-medium">Enable Reminders</p>
                      <p className="text-slate-400 text-[11px] mt-0.5">
                        Send a reminder notification before each calendar event
                      </p>
                    </div>
                    <Toggle
                      enabled={form.reminders_enabled}
                      onChange={(v) => set("reminders_enabled", v)}
                      color="#34A853"
                    />
                  </div>

                  {form.reminders_enabled && (
                    <div>
                      <label className="text-slate-400 text-xs mb-1.5 block">
                        Reminder Time (minutes before event)
                      </label>
                      <select
                        value={form.reminder_minutes_before}
                        onChange={(e) => set("reminder_minutes_before", e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#34A853]/50 transition-colors cursor-pointer appearance-none"
                      >
                        <option value="15">15 minutes before</option>
                        <option value="30">30 minutes before</option>
                        <option value="60">1 hour before</option>
                        <option value="120">2 hours before</option>
                        <option value="1440">1 day before</option>
                        <option value="2880">2 days before</option>
                      </select>
                      <p className="text-slate-300 text-[11px] mt-1">
                        Reminder will be sent this time before the event starts.
                      </p>
                    </div>
                  )}
                </div>

                {/* Event content toggles */}
                <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
                  <h3 className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
                    <i className="ri-list-check-2 text-[#34A853] text-base" />
                    Event Details to Include
                  </h3>

                  {[
                    {
                      key: "include_location" as const,
                      label: "Location / Venue",
                      desc: "Add job location to calendar event description",
                    },
                    {
                      key: "include_client_name" as const,
                      label: "Client Name",
                      desc: "Include client name in event description",
                    },
                    {
                      key: "include_notes" as const,
                      label: "Internal Notes",
                      desc: "Add job notes to event description (visible in calendar)",
                    },
                  ].map((item) => (
                    <div
                      key={item.key}
                      className="flex items-center justify-between py-2.5 border-b border-slate-200 last:border-0"
                    >
                      <div>
                        <p className="text-slate-600 text-xs font-medium">{item.label}</p>
                        <p className="text-slate-400 text-[11px]">{item.desc}</p>
                      </div>
                      <Toggle
                        enabled={form[item.key]}
                        onChange={(v) => set(item.key, v)}
                        color="#34A853"
                      />
                    </div>
                  ))}
                </div>

                {/* Save */}
                <div className="flex items-center justify-between pt-1">
                  <div>
                    {saved && !saveError && (
                      <span className="flex items-center gap-1.5 text-emerald-600 text-xs">
                        <i className="ri-check-double-line" />
                        Calendar settings saved
                      </span>
                    )}
                    {saveError && (
                      <span className="flex items-center gap-1.5 text-red-500 text-xs">
                        <i className="ri-error-warning-line" />
                        {saveError}
                      </span>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                    style={{ backgroundColor: "#34A853", color: "#fff" }}
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line" />
                        Save Calendar Settings
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* ─── Right col ─── */}
              <div className="space-y-4">

                {/* Status */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-4">Current Config</h3>
                  <div className="space-y-3">
                    {[
                      { label: "Sync", value: form.sync_enabled ? "Enabled" : "Disabled", highlight: form.sync_enabled },
                      { label: "Calendar", value: form.calendar_email || "—", highlight: false },
                      { label: "Duration", value: DURATIONS.find((d) => d.value === form.default_event_duration_minutes)?.label ?? "—", highlight: false },
                      { label: "Timezone", value: TIMEZONES.find((t) => t.value === form.timezone)?.label.split(" ")[0] ?? "—", highlight: false },
                      { label: "Title Format", value: selectedFormat?.label ?? "—", highlight: false },
                      { label: "Reminders", value: form.reminders_enabled ? `${form.reminder_minutes_before}m before` : "Off", highlight: form.reminders_enabled },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center justify-between">
                        <span className="text-slate-400 text-xs">{item.label}</span>
                        <span
                          className={`text-xs font-medium max-w-[140px] text-right truncate ${
                            item.highlight ? "text-emerald-400" : "text-slate-500"
                          }`}
                        >
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Event preview */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-4">Event Preview</h3>
                  <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-2.5">
                    {/* Calendar event mock */}
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: "#34A853" }} />
                      <p className="text-[#0f172a]/80 text-xs font-medium truncate">
                        {selectedFormat?.example ?? "Wedding · Sarah & Tom"}
                      </p>
                    </div>
                    <div className="space-y-1 pl-5">
                      <div className="flex items-center gap-1.5">
                        <i className="ri-time-line text-slate-400 text-[10px]" />
                        <span className="text-slate-400 text-[11px]">
                          2h event ·{" "}
                          {TIMEZONES.find((t) => t.value === form.timezone)?.label.split(" ")[0] ?? "ET"}
                        </span>
                      </div>
                      {form.include_location && (
                        <div className="flex items-center gap-1.5">
                          <i className="ri-map-pin-line text-slate-400 text-[10px]" />
                          <span className="text-slate-400 text-[11px]">Central Park, New York</span>
                        </div>
                      )}
                      {form.include_client_name && (
                        <div className="flex items-center gap-1.5">
                          <i className="ri-user-line text-slate-400 text-[10px]" />
                          <span className="text-slate-400 text-[11px]">Client: Sarah Johnson</span>
                        </div>
                      )}
                      {form.include_notes && (
                        <div className="flex items-center gap-1.5">
                          <i className="ri-sticky-note-line text-slate-400 text-[10px]" />
                          <span className="text-slate-400 text-[11px] italic">
                            Bring backup body + 70-200mm
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-slate-300 text-[10px] mt-2 text-center">
                    Preview — actual event depends on job data
                  </p>
                </div>

                {/* Readiness checklist */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-3">Readiness</h3>
                  <div className="space-y-2">
                    {[
                      { label: "Sync enabled",           done: form.sync_enabled },
                      { label: "Calendar email set",     done: !!form.calendar_email.trim() },
                      { label: "Timezone configured",    done: !!form.timezone },
                      { label: "Duration set",           done: !!form.default_event_duration_minutes },
                      { label: "OAuth in Supabase secrets", done: false },
                      { label: "Backend Edge Function ready", done: false },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center gap-2.5">
                        <div
                          className={`w-4 h-4 flex items-center justify-center rounded-full shrink-0 ${
                            item.done ? "bg-emerald-500/20" : "bg-white/5"
                          }`}
                        >
                          <i
                            className={`text-[10px] ${
                              item.done
                                ? "ri-check-line text-emerald-400"
                                : "ri-time-line text-slate-300"
                            }`}
                          />
                        </div>
                        <span className={`text-xs ${item.done ? "text-[#0f172a]/55" : "text-slate-300"}`}>
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Jobs sync status info */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="text-slate-400 text-xs uppercase tracking-wider mb-3">Jobs Sync Fields</h3>
                  <div className="space-y-2">
                    <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-600 text-[11px] font-mono">calendar_event_id</span>
                        <span className="text-slate-400 text-[10px]">text null</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-600 text-[11px] font-mono">calendar_sync_status</span>
                        <span className="text-slate-400 text-[10px]">text</span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-[11px] leading-relaxed">
                      Each job now tracks its Google Calendar event ID and sync status (
                      <span className="font-mono">not_synced</span> /{" "}
                      <span className="font-mono">synced</span> /{" "}
                      <span className="font-mono">sync_error</span>).
                    </p>
                  </div>
                </div>

                {/* Back link */}
                <NavLink
                  to="/admin/settings/integrations"
                  className="flex items-center gap-2 text-slate-400 hover:text-slate-600 text-xs transition-colors"
                >
                  <i className="ri-arrow-left-line text-xs" />
                  Back to Integrations
                </NavLink>
              </div>
            </div>
          </form>
        )}
      </div>
      </ModuleGate>
    </AdminLayout>
  );
}
